import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TechnicianCard from './components/TechnicianCard';
import CalendarView from './components/CalendarView';
import AssignmentForm from './components/AssignmentForm';
import FilterPanel from './components/FilterPanel';
import AssignmentDetails from './components/AssignmentDetails';
import BulkAssignModal from './components/BulkAssignModal';
import AssignmentAnalytics from './components/AssignmentAnalytics';
import AssignmentNotificationBanner from './components/AssignmentNotificationBanner';

const TechnicianAssignment = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('technicians');
  const [mainTab, setMainTab] = useState('assignments'); // 'assignments' | 'analytics'
  const [selectedTechnicians, setSelectedTechnicians] = useState([]);
  const [primaryTechnician, setPrimaryTechnician] = useState(null);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [incompleteTickets, setIncompleteTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragDropNotification, setDragDropNotification] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [conflictWarnings, setConflictWarnings] = useState([]);
  const [customerLocations, setCustomerLocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [notificationStatus, setNotificationStatus] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    skills: [],
    maxDistance: '',
    maxWorkload: '',
    sortBy: 'name',
    availableToday: false,
    locationCustomer: 'all',
    locationWell: 'all'
  });

  const mockAssignments = [
  {
    id: 'ASSIGN-001',
    ticketId: 'TKT-8745',
    scheduledDate: '2026-02-25T09:00:00',
    scheduledTime: '09:00 AM',
    estimatedDuration: '3',
    priority: 'high',
    status: 'scheduled',
    location: '123 Customer St, Downtown',
    notes: 'Customer prefers morning appointments. Bring ladder for roof access.',
    technicians: [
    {
      id: 'TECH-001',
      name: 'Michael Rodriguez',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d99ddb00-1763291684442.png",
      avatarAlt: 'Professional headshot of Hispanic male technician with short black hair wearing navy blue work uniform',
      phone: '(555) 123-4567',
      isPrimary: true
    },
    {
      id: 'TECH-003',
      name: 'James Wilson',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d3688c5d-1763295491839.png",
      avatarAlt: 'Professional headshot of Caucasian male senior technician with gray hair and beard wearing safety vest',
      phone: '(555) 345-6789',
      isPrimary: false
    }]
  },
  {
    id: 'ASSIGN-002',
    ticketId: 'TKT-8732',
    scheduledDate: '2026-02-23T14:00:00',
    scheduledTime: '02:00 PM',
    estimatedDuration: '2',
    priority: 'medium',
    status: 'in-progress',
    location: '456 Service Ave, Westside',
    notes: 'Follow-up service call. Parts already ordered.',
    technicians: [
    {
      id: 'TECH-002',
      name: 'Sarah Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17e7d9bff-1763294742502.png",
      avatarAlt: 'Professional headshot of Asian female technician with long black hair in ponytail wearing company polo shirt',
      phone: '(555) 234-5678',
      isPrimary: true
    }]
  },
  {
    id: 'ASSIGN-003',
    ticketId: 'TKT-8756',
    scheduledDate: '2026-02-26T10:30:00',
    scheduledTime: '10:30 AM',
    estimatedDuration: '4',
    priority: 'urgent',
    status: 'scheduled',
    location: '789 Emergency Rd, Eastside',
    notes: 'Emergency repair. Customer will be home all day.',
    technicians: [
    {
      id: 'TECH-004',
      name: 'Aisha Patel',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_172cfbdcf-1763301735988.png",
      avatarAlt: 'Professional headshot of Indian female technician with long dark hair wearing company uniform and safety glasses',
      phone: '(555) 456-7890',
      isPrimary: true
    }]
  }];

  const mockAvailableTickets = [
  {
    id: 'TKT-8760',
    customer: 'John Smith',
    issue: 'HVAC system not cooling properly',
    priority: 'high',
    location: '234 Residential St'
  },
  {
    id: 'TKT-8761',
    customer: 'Maria Garcia',
    issue: 'Electrical outlet not working',
    priority: 'medium',
    location: '567 Apartment Blvd'
  },
  {
    id: 'TKT-8762',
    customer: 'David Lee',
    issue: 'Plumbing leak under sink',
    priority: 'urgent',
    location: '890 House Ln'
  },
  {
    id: 'TKT-8763',
    customer: 'Jennifer Brown',
    issue: 'Appliance repair - refrigerator',
    priority: 'low',
    location: '123 Condo Way'
  }];

  useEffect(() => {
    fetchTechnicians();
    fetchIncompleteTickets();
    fetchCustomerLocations();
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const { data } = await supabase?.from('customers')?.select('id, name')?.eq('status', 'active');
      setCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchCustomerLocations = async () => {
    try {
      const { data } = await supabase?.
      from('customer_locations')?.
      select('id, customer_id, well_name, location, company, customers(name)');
      setCustomerLocations(data || []);
    } catch (err) {
      console.error('Error fetching customer locations:', err);
    }
  };

  const fetchTechnicians = async () => {
    try {
      setLoading(true);
      const { data: personnelData, error: personnelError } = await supabase?.from('personnel')?.select('*')?.eq('role', 'technician');

      if (personnelError) throw personnelError;

      const techsWithSkills = await Promise.all(
        personnelData?.map(async (person) => {
          const { data: skillsData } = await supabase?.from('personnel_skills')?.select('skill')?.eq('personnel_id', person?.id);

          const skills = skillsData?.map((s) => s?.skill) || [];

          return {
            id: person?.id,
            name: person?.name,
            avatar: person?.avatar || 'https://img.rocket.new/generatedImages/rocket_gen_img_1d99ddb00-1763291684442.png',
            avatarAlt: person?.avatar_alt || `Professional headshot of ${person?.name}`,
            status: person?.status || 'available',
            location: person?.location || 'Location not set',
            activeJobs: 0,
            workloadPercentage: 0,
            skills: skills,
            phone: person?.phone || 'N/A',
            email: person?.email || '',
            rating: parseFloat(person?.rating) || 0
          };
        }) || []
      );

      setTechnicians(techsWithSkills);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncompleteTickets = async () => {
    try {
      const { data: statusData } = await supabase?.from('ticket_statuses')?.select('id, name')?.in('name', ['Open', 'In Progress', 'Pending', 'Assigned']);

      const statusIds = statusData?.map((s) => s?.id) || [];

      if (statusIds?.length === 0) return;

      const { data: ticketsData, error } = await supabase?.from('tickets')?.select(`
          id,
          ticket_number,
          description,
          scheduled_date,
          customers (name, location),
          ticket_priorities (name, color),
          ticket_statuses (name),
          customer_locations (well_name, location)
        `)?.in('status_id', statusIds)?.order('scheduled_date', { ascending: true });

      if (error) throw error;

      const formattedTickets = ticketsData?.map((ticket) => ({
        id: ticket?.ticket_number,
        customer: ticket?.customers?.name || 'Unknown Customer',
        issue: ticket?.description || 'No description',
        priority: ticket?.ticket_priorities?.name?.toLowerCase() || 'medium',
        location: ticket?.customer_locations?.well_name || ticket?.customers?.location || 'Location not set',
        status: ticket?.ticket_statuses?.name || 'Open',
        scheduledDate: ticket?.scheduled_date
      })) || [];

      setIncompleteTickets(formattedTickets);
    } catch (error) {
      console.error('Error fetching incomplete tickets:', error);
    }
  };

  // Send Resend notification via edge function
  const sendAssignmentNotification = async (type, technicianData, ticketData, conflictDetails = null) => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-assignment-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          type,
          technicianName: technicianData?.name,
          technicianEmail: technicianData?.email,
          ticketId: ticketData?.ticketId || ticketData?.id,
          scheduledDate: ticketData?.scheduledDate,
          scheduledTime: ticketData?.scheduledTime,
          location: ticketData?.location,
          notes: ticketData?.notes,
          conflictDetails
        })
      });
      const result = await response?.json();
      if (result?.success) {
        setNotificationStatus({ type: 'success', message: `Email notification sent to ${technicianData?.name}` });
        setTimeout(() => setNotificationStatus(null), 4000);
      }
    } catch (err) {
      console.error('Notification error:', err);
    }
  };

  const handleTechnicianSelect = (technician) => {
    const isSelected = selectedTechnicians?.some((t) => t?.id === technician?.id);

    if (isSelected) {
      setSelectedTechnicians((prev) => prev?.filter((t) => t?.id !== technician?.id));
      if (primaryTechnician?.id === technician?.id) {
        setPrimaryTechnician(null);
      }
    } else {
      setSelectedTechnicians((prev) => [...prev, technician]);
      if (!primaryTechnician) {
        setPrimaryTechnician(technician);
      }
    }
  };

  const handleSetPrimary = (technician) => {
    setPrimaryTechnician(technician);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFilters((prev) => ({
      ...prev,
      skills: prev?.skills?.filter((s) => s !== skillToRemove)
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      skills: [],
      maxDistance: '',
      maxWorkload: '',
      sortBy: 'name',
      availableToday: false,
      locationCustomer: 'all',
      locationWell: 'all'
    });
  };

  const handleCreateAssignment = async (assignmentData) => {
    // Send new assignment notification to each selected technician
    for (const tech of selectedTechnicians) {
      await sendAssignmentNotification('new_assignment', tech, {
        ticketId: assignmentData?.ticketId,
        scheduledDate: assignmentData?.scheduledDate,
        scheduledTime: assignmentData?.scheduledTime,
        location: assignmentData?.location,
        notes: assignmentData?.notes
      });
    }
    setShowAssignmentForm(false);
    setSelectedTechnicians([]);
    setPrimaryTechnician(null);
  };

  const handleAssignmentClick = (assignment) => {
    setSelectedAssignment(assignment);
  };

  const handleCalendarDrop = (technician, dropDate) => {
    const dateStr = dropDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    setDragDropNotification(`${technician?.name} scheduled for ${dateStr}`);
    setSelectedDate(dropDate);
    const isSelected = selectedTechnicians?.some((t) => t?.id === technician?.id);
    if (!isSelected) {
      setSelectedTechnicians((prev) => [...prev, technician]);
      if (!primaryTechnician) {
        setPrimaryTechnician(technician);
      }
    }
    setTimeout(() => setDragDropNotification(null), 3000);
  };

  const detectConflicts = (technicianId, scheduledDate, durationHours) => {
    if (!scheduledDate || !durationHours) return [];
    const newStart = new Date(scheduledDate);
    const newEnd = new Date(newStart.getTime() + durationHours * 60 * 60 * 1000);

    return mockAssignments?.filter((assignment) => {
      const hasTech = assignment?.technicians?.some((t) => t?.id === technicianId);
      if (!hasTech) return false;
      const existStart = new Date(assignment?.scheduledDate);
      const existEnd = new Date(existStart.getTime() + parseFloat(assignment?.estimatedDuration || 1) * 60 * 60 * 1000);
      return newStart < existEnd && newEnd > existStart;
    });
  };

  const handleTicketSelect = (ticket) => {
    const isSelected = selectedTickets?.some((t) => t?.id === ticket?.id);
    if (isSelected) {
      setSelectedTickets((prev) => prev?.filter((t) => t?.id !== ticket?.id));
    } else {
      setSelectedTickets((prev) => [...prev, ticket]);
    }
  };

  const handleSelectAllTickets = () => {
    const allTickets = [...incompleteTickets, ...mockAvailableTickets];
    if (selectedTickets?.length === allTickets?.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(allTickets);
    }
  };

  const handleBulkAssign = async (technician, scheduledDate, duration) => {
    const conflicts = detectConflicts(technician?.id, scheduledDate, parseFloat(duration || 2));
    if (conflicts?.length > 0) {
      setConflictWarnings(conflicts);
      // Send conflict notification
      const conflictDetails = conflicts?.map((c) => `${c?.ticketId} at ${c?.scheduledTime} (${c?.estimatedDuration}h)`)?.join(', ');
      await sendAssignmentNotification('assignment_conflict', technician, {
        ticketId: selectedTickets?.map((t) => t?.id)?.join(', '),
        scheduledDate,
        scheduledTime: scheduledDate ? new Date(scheduledDate)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''
      }, conflictDetails);
    } else {
      setConflictWarnings([]);
      // Send new assignment notification
      await sendAssignmentNotification('new_assignment', technician, {
        ticketId: selectedTickets?.map((t) => t?.id)?.join(', '),
        scheduledDate,
        scheduledTime: scheduledDate ? new Date(scheduledDate)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '',
        location: selectedTickets?.[0]?.location
      });
    }
    setShowBulkAssignModal(false);
    setSelectedTickets([]);
  };

  // Location-based ticket filtering
  const getFilteredTickets = () => {
    if (filters?.locationWell !== 'all') {
      const selectedLoc = customerLocations?.find((l) => l?.id === filters?.locationWell);
      if (selectedLoc) {
        return incompleteTickets?.filter((t) =>
        t?.location?.toLowerCase()?.includes(selectedLoc?.well_name?.toLowerCase())
        );
      }
    }
    if (filters?.locationCustomer !== 'all') {
      const custLocs = customerLocations?.filter((l) => l?.customer_id === filters?.locationCustomer);
      const wellNames = custLocs?.map((l) => l?.well_name?.toLowerCase());
      return incompleteTickets?.filter((t) =>
      wellNames?.some((wn) => t?.location?.toLowerCase()?.includes(wn))
      );
    }
    return incompleteTickets;
  };

  const filteredTechnicians = technicians?.filter((tech) => {
    if (filters?.search && !tech?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
      return false;
    }
    if (filters?.status !== 'all' && tech?.status !== filters?.status) {
      return false;
    }
    if (filters?.skills?.length > 0 && !filters?.skills?.some((skill) => tech?.skills?.some((techSkill) => techSkill?.toLowerCase()?.includes(skill?.toLowerCase())))) {
      return false;
    }
    if (filters?.maxWorkload && tech?.workloadPercentage > parseInt(filters?.maxWorkload)) {
      return false;
    }
    if (filters?.availableToday && tech?.status !== 'available') {
      return false;
    }
    return true;
  });

  const todayAssignments = mockAssignments?.filter((assignment) => {
    const today = new Date();
    const assignmentDate = new Date(assignment.scheduledDate);
    return assignmentDate?.getDate() === today?.getDate() &&
    assignmentDate?.getMonth() === today?.getMonth() &&
    assignmentDate?.getFullYear() === today?.getFullYear();
  });

  const selectedDateAssignments = mockAssignments?.filter((assignment) => {
    const assignmentDate = new Date(assignment.scheduledDate);
    return assignmentDate?.getDate() === selectedDate?.getDate() &&
    assignmentDate?.getMonth() === selectedDate?.getMonth() &&
    assignmentDate?.getFullYear() === selectedDate?.getFullYear();
  });

  const isSelectedDateToday = selectedDate?.toDateString() === new Date()?.toDateString();
  const allTickets = [...incompleteTickets, ...mockAvailableTickets];
  const displayedTickets = getFilteredTickets();

  // Active location filter label
  const activeLocationFilter = filters?.locationWell !== 'all' ?
  customerLocations?.find((l) => l?.id === filters?.locationWell)?.well_name :
  filters?.locationCustomer !== 'all' ?
  customers?.find((c) => c?.id === filters?.locationCustomer)?.name :
  null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
                Technician Assignment
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Assign qualified technicians to service tickets and manage schedules
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Filter"
                iconPosition="left"
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden">
                Filters
              </Button>
              {selectedTickets?.length > 0 &&
              <Button
                variant="secondary"
                iconName="Users"
                iconPosition="left"
                onClick={() => setShowBulkAssignModal(true)}>
                  Bulk Assign ({selectedTickets?.length})
                </Button>
              }
              {selectedTechnicians?.length > 0 &&
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowAssignmentForm(true)}>
                  Create Assignment ({selectedTechnicians?.length})
                </Button>
              }
            </div>
          </div>

          {/* Main Tab: Assignments / Analytics */}
          <div className="flex gap-1 bg-muted/50 rounded-lg p-1 w-fit mb-4">
            <button
              onClick={() => setMainTab('assignments')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
              mainTab === 'assignments' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`
              }>
              
              <Icon name="CalendarCheck" size={16} color="currentColor" />
              Assignments
            </button>
            <button
              onClick={() => setMainTab('analytics')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
              mainTab === 'analytics' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`
              }>
              
              <Icon name="BarChart2" size={16} color="currentColor" />
              Analytics
            </button>
          </div>

          {dragDropNotification &&
          <div className="mb-4 bg-success/10 border border-success/30 rounded-lg p-3 flex items-center gap-2">
              <Icon name="CheckCircle" size={18} color="var(--color-success)" />
              <span className="text-sm font-medium text-success">{dragDropNotification}</span>
            </div>
          }

          {notificationStatus &&
          <div className={`mb-4 rounded-lg p-3 flex items-center gap-2 ${
          notificationStatus?.type === 'success' ? 'bg-success/10 border border-success/30' : 'bg-error/10 border border-error/30'}`
          }>
              <Icon
              name={notificationStatus?.type === 'success' ? 'Mail' : 'AlertTriangle'}
              size={18}
              color={notificationStatus?.type === 'success' ? 'var(--color-success)' : 'var(--color-error)'} />
            
              <span className={`text-sm font-medium ${
            notificationStatus?.type === 'success' ? 'text-success' : 'text-error'}`
            }>{notificationStatus?.message}</span>
            </div>
          }

          {conflictWarnings?.length > 0 &&
          <div className="mb-4 bg-error/10 border border-error/30 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={18} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-error mb-1">Assignment Conflict Detected</p>
                  <p className="text-xs text-error/80 mb-2">The selected technician already has assignments during the requested time slot:</p>
                  <ul className="space-y-1">
                    {conflictWarnings?.map((conflict) =>
                  <li key={conflict?.id} className="text-xs text-error/80 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-error flex-shrink-0" />
                        {conflict?.ticketId} — {new Date(conflict?.scheduledDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {conflict?.scheduledTime} ({conflict?.estimatedDuration}h)
                      </li>
                  )}
                  </ul>
                </div>
                <button
                onClick={() => setConflictWarnings([])}
                className="text-error/60 hover:text-error transition-smooth">
                
                  <Icon name="X" size={16} color="currentColor" />
                </button>
              </div>
            </div>
          }

          {selectedTechnicians?.length > 0 &&
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Users" size={20} color="var(--color-primary)" />
                  <span className="text-sm font-medium text-foreground">
                    {selectedTechnicians?.length} technician{selectedTechnicians?.length !== 1 ? 's' : ''} selected
                  </span>
                  {primaryTechnician &&
                <span className="text-sm text-muted-foreground">
                      • Primary: {primaryTechnician?.name}
                    </span>
                }
                </div>
                <button
                onClick={() => {
                  setSelectedTechnicians([]);
                  setPrimaryTechnician(null);
                }}
                className="text-sm text-primary hover:text-primary/80 font-medium transition-smooth">
                  Clear Selection
                </button>
              </div>
            </div>
          }

          {/* Active location filter badge */}
          {activeLocationFilter &&
          <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Filtering by location:</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                <Icon name="MapPin" size={12} color="currentColor" />
                {activeLocationFilter}
                <button
                onClick={() => {
                  handleFilterChange('locationCustomer', 'all');
                  handleFilterChange('locationWell', 'all');
                }}
                className="hover:bg-primary/20 rounded-full p-0.5 ml-0.5">
                
                  <Icon name="X" size={10} color="currentColor" />
                </button>
              </span>
            </div>
          }

          {filters?.skills?.length > 0 &&
          <div className="mt-4 bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-foreground">Active Skill Filters</h3>
                <button
                onClick={() => handleFilterChange('skills', [])}
                className="text-xs text-primary hover:text-primary/80 font-medium transition-smooth">
                
                  Clear All Skills
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {filters?.skills?.map((skill) =>
              <div
                key={skill}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                
                    <span className="capitalize">{skill}</span>
                    <button
                  onClick={() => handleRemoveSkill(skill)}
                  className="hover:bg-primary/20 rounded-full p-0.5 transition-smooth"
                  aria-label={`Remove ${skill} filter`}>
                  
                      <Icon name="X" size={14} color="currentColor" />
                    </button>
                  </div>
              )}
              </div>
            </div>
          }
        </div>

        {/* Analytics Tab */}
        {mainTab === 'analytics' &&
        <AssignmentAnalytics technicians={technicians} assignments={mockAssignments} />
        }

        {mainTab === 'assignments' &&
        <>
            <div className="lg:hidden mb-4">
              <div className="flex gap-2 border-b border-border">
                <button
                onClick={() => setActiveTab('technicians')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-smooth ${
                activeTab === 'technicians' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`
                }>
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="Users" size={18} color="currentColor" />
                    Technicians
                  </div>
                </button>
                <button
                onClick={() => setActiveTab('calendar')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-smooth ${
                activeTab === 'calendar' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`
                }>
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="Calendar" size={18} color="currentColor" />
                    Calendar
                  </div>
                </button>
              </div>
            </div>

            {showFilters &&
          <div className="lg:hidden mb-6">
                <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              customerLocations={customerLocations}
              customers={customers} />
              </div>
          }

            {showAssignmentForm ?
          <AssignmentForm
            selectedTechnicians={selectedTechnicians}
            primaryTechnician={primaryTechnician}
            availableTickets={mockAvailableTickets}
            onSubmit={handleCreateAssignment}
            onCancel={() => setShowAssignmentForm(false)} /> :

          selectedAssignment ?
          <AssignmentDetails
            assignment={selectedAssignment}
            onClose={() => setSelectedAssignment(null)}
            onEdit={() => {
              setSelectedAssignment(null);
              setShowAssignmentForm(true);
            }}
            onCancel={() => {
              setSelectedAssignment(null);
            }} /> :

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left column: Filters + Available Technicians */}
                <div className="hidden lg:flex lg:col-span-3 flex-col gap-6">
                  <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
                customerLocations={customerLocations}
                customers={customers} />

                  {/* Available Technicians - below filters */}
                  <div className="bg-card rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-base font-heading font-semibold text-foreground">
                        Available Technicians
                      </h2>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                        {filteredTechnicians?.length}
                      </span>
                    </div>

                    <div className="space-y-3 max-h-[calc(100vh-32rem)] overflow-y-auto">
                      {loading ?
                  <div className="text-center py-8">
                          <Icon name="Loader" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3 animate-spin" />
                          <p className="text-sm text-muted-foreground">Loading...</p>
                        </div> :
                  filteredTechnicians?.length > 0 ?
                  filteredTechnicians?.map((technician) =>
                  <TechnicianCard
                    key={technician?.id}
                    technician={technician}
                    isSelected={selectedTechnicians?.some((t) => t?.id === technician?.id)}
                    isPrimary={primaryTechnician?.id === technician?.id}
                    onSelect={() => handleTechnicianSelect(technician)}
                    onSetPrimary={() => handleSetPrimary(technician)}
                    distance={Math.floor(Math.random() * 20) + 1} />
                  ) :
                  <div className="text-center py-8">
                          <Icon name="Users" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                          <p className="text-sm text-muted-foreground">No technicians match filters</p>
                          <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetFilters}
                      className="mt-3">
                            Reset
                          </Button>
                        </div>
                  }
                    </div>
                  </div>
                </div>

                {/* Middle column: Today's Assignments */}
                <div className={`lg:col-span-4 space-y-6 ${activeTab === 'technicians' ? 'block' : 'hidden lg:block'}`}>
                  {/* Today's Assignments */}
                  <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Icon name="CalendarCheck" size={20} color="var(--color-primary)" />
                        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                          Today's Assignments
                        </h3>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {todayAssignments?.length > 0 ? todayAssignments?.map((assignment) =>
                  <div
                    key={assignment?.id}
                    className="p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-smooth border border-border"
                    onClick={() => handleAssignmentClick(assignment)}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium text-foreground text-sm">{assignment?.ticketId}</p>
                              <p className="text-xs text-muted-foreground">{assignment?.scheduledTime}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                      assignment?.priority === 'urgent' ? 'bg-error/10 text-error' :
                      assignment?.priority === 'high' ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'}`
                      }>
                              {assignment?.priority?.charAt(0)?.toUpperCase() + assignment?.priority?.slice(1)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Icon name="Users" size={12} color="currentColor" />
                            <span>{assignment?.technicians?.length} technician{assignment?.technicians?.length !== 1 ? 's' : ''}</span>
                            <span className="mx-1">•</span>
                            <Icon name="MapPin" size={12} color="currentColor" />
                            <span className="truncate">{assignment?.location}</span>
                          </div>
                        </div>
                  ) :
                  <div className="text-center py-6">
                          <Icon name="CalendarCheck" size={36} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">No assignments scheduled for today</p>
                        </div>
                  }
                    </div>
                  </div>

                  {/* Mobile: Available Technicians */}
                  <div className="lg:hidden bg-card rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-base font-heading font-semibold text-foreground">Available Technicians</h2>
                      <span className="text-xs text-muted-foreground">{filteredTechnicians?.length} found</span>
                    </div>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {loading ?
                  <div className="text-center py-6">
                          <Icon name="Loader" size={28} color="var(--color-muted-foreground)" className="mx-auto mb-2 animate-spin" />
                        </div> :
                  filteredTechnicians?.map((technician) =>
                  <TechnicianCard
                    key={technician?.id}
                    technician={technician}
                    isSelected={selectedTechnicians?.some((t) => t?.id === technician?.id)}
                    isPrimary={primaryTechnician?.id === technician?.id}
                    onSelect={() => handleTechnicianSelect(technician)}
                    onSetPrimary={() => handleSetPrimary(technician)}
                    distance={Math.floor(Math.random() * 20) + 1} />
                  )}
                    </div>
                  </div>
                </div>

                {/* Right column: Calendar */}
                <div className={`lg:col-span-5 ${activeTab === 'calendar' ? 'block' : 'hidden lg:block'}`}>
                  <CalendarView
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                assignments={mockAssignments}
                onAssignmentClick={handleAssignmentClick}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onDrop={handleCalendarDrop} />

                  {!isSelectedDateToday &&
              <div className="mt-6 bg-card rounded-lg border border-border p-4 md:p-6">
                      <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
                        Assignments for {selectedDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </h3>
                      <div className="space-y-3">
                        {selectedDateAssignments?.map((assignment) =>
                  <div
                    key={assignment?.id}
                    className="p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-smooth"
                    onClick={() => handleAssignmentClick(assignment)}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium text-foreground">{assignment?.ticketId}</p>
                              <p className="text-sm text-muted-foreground">{assignment?.scheduledTime}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                      assignment?.priority === 'urgent' ? 'bg-error/10 text-error' :
                      assignment?.priority === 'high' ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'}`
                      }>
                              {assignment?.priority?.charAt(0)?.toUpperCase() + assignment?.priority?.slice(1)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon name="Users" size={14} color="currentColor" />
                            <span>{assignment?.technicians?.length} technician{assignment?.technicians?.length !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                  )}
                        {selectedDateAssignments?.length === 0 &&
                  <div className="text-center py-8">
                          <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                          <p className="text-muted-foreground">No assignments scheduled for this date</p>
                        </div>
                  }
                      </div>
                    </div>
              }
                </div>
              </div>
          }

            {!showAssignmentForm && !selectedAssignment &&
          <div className="mt-6 bg-card rounded-lg border border-border p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                      Incomplete Tickets
                    </h2>
                    <span className="text-sm text-muted-foreground">
                      {displayedTickets?.length} tickets
                      {activeLocationFilter && ` at ${activeLocationFilter}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {selectedTickets?.length > 0 &&
                <span className="text-sm text-primary font-medium">
                        {selectedTickets?.length} selected
                      </span>
                }
                    {displayedTickets?.length > 0 &&
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAllTickets}>
                  
                        {selectedTickets?.length === allTickets?.length ? 'Deselect All' : 'Select All'}
                      </Button>
                }
                    {selectedTickets?.length > 0 &&
                <Button
                  variant="default"
                  size="sm"
                  iconName="Users"
                  iconPosition="left"
                  onClick={() => setShowBulkAssignModal(true)}>
                  
                        Bulk Assign ({selectedTickets?.length})
                      </Button>
                }
                  </div>
                </div>
                
                {displayedTickets?.length > 0 ?
            <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="py-3 px-4 w-10">
                            <input
                        type="checkbox"
                        checked={selectedTickets?.length === allTickets?.length && allTickets?.length > 0}
                        onChange={handleSelectAllTickets}
                        className="rounded border-border" />
                      
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Ticket ID</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Issue</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Priority</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedTickets?.map((ticket) =>
                  <tr
                    key={ticket?.id}
                    className={`border-b border-border hover:bg-muted/50 transition-smooth cursor-pointer ${
                    selectedTickets?.some((t) => t?.id === ticket?.id) ? 'bg-primary/5' : ''}`
                    }
                    onClick={() => handleTicketSelect(ticket)}>
                    
                            <td className="py-3 px-4">
                            <input
                        type="checkbox"
                        checked={selectedTickets?.some((t) => t?.id === ticket?.id)}
                        onChange={() => handleTicketSelect(ticket)}
                        onClick={(e) => e?.stopPropagation()}
                        className="rounded border-border" />
                      
                            </td>
                            <td className="py-3 px-4 text-sm font-medium text-foreground">{ticket?.id}</td>
                            <td className="py-3 px-4 text-sm text-foreground">{ticket?.customer}</td>
                            <td className="py-3 px-4 text-sm text-muted-foreground max-w-xs truncate">{ticket?.issue}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                      ticket?.priority === 'urgent' ? 'bg-error/10 text-error' :
                      ticket?.priority === 'high' ? 'bg-warning/10 text-warning' :
                      ticket?.priority === 'medium' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`
                      }>
                                {ticket?.priority?.charAt(0)?.toUpperCase() + ticket?.priority?.slice(1)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-foreground">{ticket?.status}</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Icon name="MapPin" size={12} color="currentColor" />
                                <span className="truncate max-w-32">{ticket?.location}</span>
                              </div>
                            </td>
                          </tr>
                  )}
                      </tbody>
                    </table>
                  </div> :

            <div className="text-center py-12">
                    <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-4" />
                    <p className="text-muted-foreground">No incomplete tickets found</p>
                    <p className="text-sm text-muted-foreground mt-2">All tickets are completed or closed</p>
                  </div>
            }
              </div>
          }
          </>
        }
      </main>

      {showBulkAssignModal &&
      <BulkAssignModal
        selectedTickets={selectedTickets}
        technicians={filteredTechnicians}
        assignments={mockAssignments}
        onAssign={handleBulkAssign}
        onClose={() => setShowBulkAssignModal(false)}
        detectConflicts={detectConflicts} />
      }

      <AssignmentNotificationBanner />
    </div>);


};

export default TechnicianAssignment;